import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] pt-20">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4"></i>
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">瓜料不见了</h1>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
            返回首页继续吃瓜
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 4);

  return (
    <main>
      <Navbar />

      <article>
        <section className="bg-background-100 pt-24 pb-16 md:pb-20">
          <div className="px-6 md:px-14">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-2/3">
                <div className="mb-6">
                  {article.tag && (
                    <span className="inline-block px-3 py-1 rounded-full bg-foreground-950 text-background-50 text-xs font-medium mb-4">
                      {article.tag}
                    </span>
                  )}
                  <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                    {article.title}
                  </h1>
                  <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-xs text-foreground-400">
                    <span>{article.date}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views} 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line"></i>
                      {article.comments.toLocaleString()} 评论
                    </span>
                  </div>
                </div>

                <div
                  className="prose-custom text-foreground-800 text-sm md:text-base leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-10 pt-8 border-t border-background-200/70">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                      查看更多{category.name}
                    </Link>
                  )}
                </div>
              </div>

              <div className="lg:w-1/3">
                <div className="flex gap-4">
                  <div className="flex-1 rounded-2xl overflow-hidden h-[240px]">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={`${article.title} - 今日大瓜`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 rounded-2xl overflow-hidden h-[240px]">
                    <img
                      src={article.heroImageUrl}
                      alt={article.title}
                      title={`${article.title} - 今日大瓜配图`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20">
            <div className="px-6 md:px-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">猜你喜欢</h2>
              <div
                className="flex gap-5 overflow-x-auto pb-4"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {relatedArticles.map((ra, idx) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    imageUrl={ra.imageUrl}
                    tag={ra.tag}
                    date={ra.date}
                    views={ra.views}
                    isHot={ra.isHot}
                    isFirst={idx === 0}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-16 md:py-20 bg-background-100">
          <div className="px-6 md:px-14">
            <div className="max-w-xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                吃瓜不过瘾？
              </h2>
              <p className="text-foreground-500 text-sm mb-8">
                订阅每日瓜报，新鲜瓜料第一时间送到你手中
              </p>
              <form
                data-readdy-form
                action="https://readdy.ai/api/form/d913mus6ci2plld1oi90"
                method="POST"
                encType="application/x-www-form-urlencoded"
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-16"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="输入你的邮箱"
                  required
                  className="flex-1 px-5 py-3.5 rounded-full bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 border border-background-200/70 focus:outline-none focus:border-primary-300 transition-colors"
                />
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute opacity-0 pointer-events-none"
                />
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  免费订阅
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>

            <div className="w-full h-[160px] md:h-[220px] overflow-hidden rounded-3xl bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Wide%20editorial%20banner%20with%20abstract%20warm%20toned%20layered%20textures%2C%20cream%20and%20amber%20palette%2C%20minimalist%20clean%20composition%2C%20soft%20elegant%20magazine%20aesthetic%2C%20no%20text&width=1600&height=400&seq=article-cta-banner&orientation=landscape"
                alt="订阅今日大瓜"
                title="订阅每日瓜报 - 今日大瓜"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </article>

      <Footer />
    </main>
  );
}